import React, { useState, useMemo, useEffect } from 'react';
import { Card, InputNumber, Input, Button, Typography, Statistic, Row, Col, Space, Popconfirm } from 'antd';
import { PlusOutlined, DeleteOutlined, AppstoreOutlined, ClearOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { numericInputProps } from '../utils/formatters';
import { calcMarketingAmount, sumCategoryItems, sumBudget } from '../utils/budget';
import type { BudgetItem, BudgetCategory } from '../utils/budget';

export type { BudgetCategory };

interface BudgetProps {
  cogs?: number;
  /** Full unit cost for buyouts: COGS + FBA + referral fee + other fees + storage */
  fullUnitCost?: number;
  initialData?: BudgetCategory[];
  onDataChange?: (data: BudgetCategory[]) => void;
}

type Translator = (key: string) => string;

function makeDefaults(t: Translator): BudgetCategory[] {
  return [
    {
      id: 'development',
      name: t('idea_calc.budget.cat_development'),
      color: '#00b746',
      items: [
        { id: '1', name: t('idea_calc.budget.default_warehouse'), amount: 50 },
        { id: '2', name: t('idea_calc.budget.default_buyer'), amount: 50 },
      ],
    },
    {
      id: 'prelaunch',
      name: t('idea_calc.budget.cat_prelaunch'),
      color: '#007bff',
      items: [
        { id: '3', name: t('idea_calc.budget.default_seo'), amount: 50 },
        { id: '4', name: t('idea_calc.budget.default_3d'), amount: 250 },
        { id: '5', name: t('idea_calc.budget.default_design_brief'), amount: 50 },
        { id: '6', name: t('idea_calc.budget.default_listing_design'), amount: 300 },
        { id: '7', name: t('idea_calc.budget.default_ppc_setup'), amount: 50 },
        { id: '8', name: t('idea_calc.budget.default_video'), amount: 50 },
        { id: '9', name: t('idea_calc.budget.default_account_manager'), amount: 50 },
      ],
    },
    {
      id: 'marketing',
      name: t('idea_calc.budget.cat_marketing'),
      color: '#f3af00',
      items: [
        { id: '10', name: t('idea_calc.budget.default_reviews_buyout'), amount: 0, marketingType: 'reviews', quantity: 10, unitCost: 15, commissionPercent: 0 },
        { id: '11', name: t('idea_calc.budget.default_simple_buyout'), amount: 0, marketingType: 'buyout', quantity: 15, unitCost: 5, commissionPercent: 0 },
        { id: '12', name: t('idea_calc.budget.default_vine'), amount: 0, marketingType: 'vine', quantity: 0, vinePrice: 0 },
      ],
    },
    {
      id: 'monthly',
      name: t('idea_calc.budget.cat_monthly'),
      color: '#8c8c8c',
      items: [
        { id: '13', name: 'Seller Central', amount: 39.99 },
        { id: '14', name: t('idea_calc.budget.default_storage'), amount: 0 },
        { id: '15', name: t('idea_calc.budget.default_other'), amount: 50 },
      ],
    },
  ];
}

const Budget: React.FC<BudgetProps> = ({ cogs = 5.15, fullUnitCost = 0, initialData, onDataChange }) => {
  const { t, i18n } = useTranslation();
  // Track if user has made changes (dirty flag to prevent unwanted resets)
  const isDirtyRef = React.useRef(false);
  // Track the last applied initialData to avoid re-applying same data
  const appliedInitialRef = React.useRef<string | null>(null);

  // Initialize with defaults if no data provided
  const [categories, setCategories] = useState<BudgetCategory[]>(() => {
    if (initialData && initialData.length > 0 && initialData.some((c) => c.items.length > 0)) {
      appliedInitialRef.current = JSON.stringify(initialData);
      return initialData;
    }
    return makeDefaults(t);
  });

  // Re-translate defaults when locale changes (Hub propagates LOCALE_CHANGE
  // after Budget mounts, so the initial defaults are in whatever locale was
  // active at mount). Only re-apply if user hasn't edited and we're still
  // on default content (no loaded calculation either).
  useEffect(() => {
    if (isDirtyRef.current) return;
    if (initialData && initialData.length > 0 && initialData.some((c) => c.items.length > 0)) return;
    // Skip first render — useState initializer already set defaults in current locale
    // i18n.language event fires only when language actually changes
    setCategories((prev) => {
      // Preserve item amounts/flags edited by user-as-default-data; just retranslate names
      const fresh = makeDefaults(t);
      // Replace category and item names while preserving any user changes to amounts.
      // We assume same shape — fresh defaults are authoritative on labels.
      return fresh.map((freshCat, idx) => {
        const prevCat = prev[idx];
        if (!prevCat) return freshCat;
        return {
          ...freshCat,
          items: freshCat.items.map((freshItem, j) => ({
            ...freshItem,
            ...(prevCat.items[j] ? { amount: prevCat.items[j].amount, quantity: prevCat.items[j].quantity, unitCost: prevCat.items[j].unitCost, vinePrice: prevCat.items[j].vinePrice, commissionPercent: prevCat.items[j].commissionPercent } : {}),
          })),
        };
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  useEffect(() => {
    // Skip if user has made edits
    if (isDirtyRef.current) return;
    if (!initialData || initialData.length === 0 || !initialData.some((c) => c.items.length > 0)) return;

    // Skip if this is the same data we already applied (compare by value)
    const serialized = JSON.stringify(initialData);
    if (appliedInitialRef.current === serialized) return;

    appliedInitialRef.current = serialized;
    setCategories(initialData);
  }, [initialData]);

  // Notify parent of data changes (use stable callback reference check)
  const onDataChangeRef = React.useRef(onDataChange);
  onDataChangeRef.current = onDataChange;

  useEffect(() => {
    onDataChangeRef.current?.(categories);
  }, [categories]);

  const isEmpty = useMemo(
    () => categories.every((cat) => cat.items.length === 0),
    [categories],
  );

  const fillDefaults = () => {
    isDirtyRef.current = true;
    setCategories(
      makeDefaults(t).map((cat) => ({
        ...cat,
        items: cat.items.map((item) => ({ ...item, id: `${Date.now()}-${item.id}` })),
      })),
    );
  };

  const clearAllAmounts = () => {
    isDirtyRef.current = true;
    setCategories((prev) =>
      prev.map((cat) => ({
        ...cat,
        items: cat.items.map((item) => ({
          ...item,
          amount: 0,
          quantity: item.marketingType ? 0 : item.quantity,
          unitCost: item.marketingType ? 0 : item.unitCost,
          commissionPercent: item.marketingType ? 0 : item.commissionPercent,
          vinePrice: item.marketingType === 'vine' ? 0 : item.vinePrice,
        })),
      })),
    );
  };

  const addItem = (categoryId: string) => {
    isDirtyRef.current = true;
    setCategories((prev) =>
      prev.map((cat) => {
        if (cat.id === categoryId) {
          return {
            ...cat,
            items: [...cat.items, { id: Date.now().toString(), name: '', amount: 0 }],
          };
        }
        return cat;
      }),
    );
  };

  const updateItem = (categoryId: string, itemId: string, field: string, value: string | number) => {
    isDirtyRef.current = true;
    setCategories((prev) =>
      prev.map((cat) => {
        if (cat.id === categoryId) {
          return {
            ...cat,
            items: cat.items.map((item) => {
              if (item.id === itemId) {
                if (field === 'name') return { ...item, name: value as string };
                return { ...item, [field]: typeof value === 'number' ? value : parseFloat(value as string) || 0 };
              }
              return item;
            }),
          };
        }
        return cat;
      }),
    );
  };

  const removeItem = (categoryId: string, itemId: string) => {
    isDirtyRef.current = true;
    setCategories((prev) =>
      prev.map((cat) => {
        if (cat.id === categoryId) {
          return { ...cat, items: cat.items.filter((item) => item.id !== itemId) };
        }
        return cat;
      }),
    );
  };

  const getItemAmount = (item: BudgetItem) => calcMarketingAmount(item, cogs, fullUnitCost);

  const getCategoryTotal = (items: BudgetItem[]) => sumCategoryItems(items, cogs, fullUnitCost);

  const totalBudget = useMemo(
    () => sumBudget(categories, cogs, fullUnitCost),
    [categories, cogs, fullUnitCost],
  );

  const renderRegularItem = (categoryId: string, item: BudgetItem) => (
    <Space.Compact key={item.id} style={{ width: '100%' }}>
      <Input
        size="small"
        placeholder={t('idea_calc.budget.item_placeholder')}
        value={item.name}
        onChange={(e) => updateItem(categoryId, item.id, 'name', e.target.value)}
        style={{ flex: '0 1 55%', fontSize: 11, minWidth: 0 }}
      />
      <InputNumber
        size="small"
        min={0}
        step={0.01}
        controls={false}
        value={item.amount}
        onChange={(v) => updateItem(categoryId, item.id, 'amount', v ?? 0)}
        style={{ flex: '1 0 auto', width: 85 }}
        prefix="$"
        {...numericInputProps}
      />
      <Popconfirm
        title={t('idea_calc.budget.delete_confirm')}
        onConfirm={() => removeItem(categoryId, item.id)}
        okText={t('idea_calc.common.yes')}
        cancelText={t('idea_calc.common.no')}
      >
        <Button size="small" danger icon={<DeleteOutlined />} style={{ width: 24 }} />
      </Popconfirm>
    </Space.Compact>
  );

  const smLabel: React.CSSProperties = { fontSize: 9, color: '#999', lineHeight: 1 };
  const smInput: React.CSSProperties = { width: '100%' };

  const renderMarketingItem = (categoryId: string, item: BudgetItem) => {
    const computed = getItemAmount(item);
    const isVine = item.marketingType === 'vine';
    const isBuyout = item.marketingType === 'buyout' || item.marketingType === 'reviews';
    // Per-unit cost for buyouts: service price + full unit cost
    const perUnitTotal = isBuyout ? (item.unitCost || 0) + fullUnitCost : 0;

    return (
      <div key={item.id} style={{ marginBottom: 4 }}>
        {/* Row 1: name + total + delete */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Input
            size="small"
            value={item.name}
            onChange={(e) => updateItem(categoryId, item.id, 'name', e.target.value)}
            style={{ flex: '0 1 55%', fontSize: 11, minWidth: 0 }}
          />
          <div style={{ flex: '1 0 auto', textAlign: 'right', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', paddingRight: 2 }}>
            ${computed.toFixed(2)}
          </div>
          <Popconfirm
            title={t('idea_calc.budget.delete_confirm')}
            onConfirm={() => removeItem(categoryId, item.id)}
            okText={t('idea_calc.common.yes')}
            cancelText={t('idea_calc.common.no')}
          >
            <Button size="small" danger icon={<DeleteOutlined />} style={{ width: 24, flexShrink: 0 }} />
          </Popconfirm>
        </div>
        {/* Row 2: breakdown inputs */}
        <div style={{ display: 'flex', gap: 4, alignItems: 'end', marginTop: 2, paddingLeft: 4 }}>
          <div style={{ width: 50 }}>
            <div style={smLabel}>{t('idea_calc.budget.label_quantity')}</div>
            <InputNumber
              size="small"
              min={0}
              step={1}
              controls={false}
              value={item.quantity ?? 0}
              onChange={(v) => updateItem(categoryId, item.id, 'quantity', v ?? 0)}
              style={smInput}
              {...numericInputProps}
            />
          </div>
          {isVine ? (
            <>
              <Typography.Text type="secondary" style={{ fontSize: 10, lineHeight: '24px' }}>
                &times; COGS(${cogs.toFixed(2)}) +
              </Typography.Text>
              <div style={{ width: 60 }}>
                <div style={smLabel}>{t('idea_calc.budget.label_vine_price')}</div>
                <InputNumber
                  size="small"
                  min={0}
                  step={1}
                  controls={false}
                  value={item.vinePrice ?? 0}
                  onChange={(v) => updateItem(categoryId, item.id, 'vinePrice', v ?? 0)}
                  style={smInput}
                  prefix="$"
                  {...numericInputProps}
                />
              </div>
            </>
          ) : (
            <>
              <Typography.Text type="secondary" style={{ fontSize: 10, lineHeight: '24px' }}>&times; (</Typography.Text>
              <div style={{ width: 55 }}>
                <div style={smLabel}>{t('idea_calc.budget.label_service')}</div>
                <InputNumber
                  size="small"
                  min={0}
                  step={0.01}
                  controls={false}
                  value={item.unitCost ?? 0}
                  onChange={(v) => updateItem(categoryId, item.id, 'unitCost', v ?? 0)}
                  style={smInput}
                  prefix="$"
                  {...numericInputProps}
                />
              </div>
              <Typography.Text type="secondary" style={{ fontSize: 10, lineHeight: '24px' }}>+ ${fullUnitCost.toFixed(2)})</Typography.Text>
              <div style={{ width: 45 }}>
                <div style={smLabel}>{t('idea_calc.budget.label_commission')}</div>
                <InputNumber
                  size="small"
                  min={0}
                  max={100}
                  step={1}
                  controls={false}
                  value={item.commissionPercent ?? 0}
                  onChange={(v) => updateItem(categoryId, item.id, 'commissionPercent', v ?? 0)}
                  style={smInput}
                  suffix="%"
                  {...numericInputProps}
                />
              </div>
            </>
          )}
        </div>
        {/* Row 3: per-unit breakdown for buyouts */}
        {isBuyout && fullUnitCost > 0 && (
          <Typography.Text type="secondary" style={{ fontSize: 9, paddingLeft: 4, display: 'block' }}>
            {t('idea_calc.budget.per_unit_breakdown', { total: perUnitTotal.toFixed(2) })}
          </Typography.Text>
        )}
      </div>
    );
  };

  return (
    <Card
      title={t('idea_calc.budget.title')}
      variant="outlined"
      style={{ height: '100%', flex: 1 }}
      extra={
        <Space size={8}>
          <Button size="small" icon={<AppstoreOutlined />} onClick={fillDefaults}>
            {t('idea_calc.budget.default_button')}
          </Button>
          {!isEmpty && (
            <Button size="small" icon={<ClearOutlined />} onClick={clearAllAmounts}>
              {t('idea_calc.budget.clear_all_button')}
            </Button>
          )}
          <Statistic
            value={totalBudget}
            precision={0}
            prefix="$"
            valueStyle={{ fontSize: 14, fontWeight: 600 }}
          />
        </Space>
      }
    >
      <Row gutter={[8, 8]}>
        {categories.map((category) => (
          <Col key={category.id} span={12}>
            <div
              style={{
                background: '#fafafa',
                borderRadius: 6,
                padding: '6px 8px',
                border: '1px solid #f0f0f0',
                minHeight: 80,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                <div style={{ width: 8, height: 8, borderRadius: 2, background: category.color, flexShrink: 0 }} />
                <Typography.Text strong style={{ fontSize: 11 }}>{category.name}</Typography.Text>
                <Typography.Text type="secondary" style={{ marginLeft: 'auto', fontSize: 11 }}>
                  ${getCategoryTotal(category.items).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                </Typography.Text>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {category.items.map((item) =>
                  item.marketingType
                    ? renderMarketingItem(category.id, item)
                    : renderRegularItem(category.id, item),
                )}
              </div>

              <Button
                type="dashed"
                icon={<PlusOutlined />}
                size="small"
                block
                style={{ marginTop: 3, fontSize: 10, height: 22 }}
                onClick={() => addItem(category.id)}
              >
                {t('idea_calc.budget.add_item')}
              </Button>
            </div>
          </Col>
        ))}
      </Row>
    </Card>
  );
};

export default React.memo(Budget);
